import { FlexColumnCenterVertical } from '@main/core-ui';
import React from 'react';
import { useTheme } from 'styled-components';

import { CheckboxInput } from '../CheckboxInput';

interface CheckboxGroupOption<T extends string> {
  /** option label */
  label: string;
  /** value passed to onChange */
  value: T;
}

/**
 * type of object passed to RadioGroup options prop
 */
export type CheckboxGroupOptions<T extends string> = Array<
  CheckboxGroupOption<T>
>;

export interface CheckboxGroupProps<
  T extends string,
  K extends Array<CheckboxGroupOption<T>>,
  J = K[number]['value'],
> extends Omit<
    React.ComponentPropsWithRef<'fieldset'>,
    'children' | 'onChange' | 'type' | 'title'
  > {
  /** current checked value of group */
  value: Array<J> | undefined;
  /** title of radio group */
  title?: string;
  /** description of title */
  titleDescription?: string | React.ReactNode;
  /** individual radio group items */
  options: K;
  /** callback for when a checkbox input is checked */
  onChange?: (value: Array<J>) => void;
  /** Flex direction of the radio group */
  direction?: 'row' | 'column';
  /** flag for whether radio input is disabled */
  disabled?: boolean;
  /** name of the radio group */
  name: string;
  /** show checkmark */
  showCheckmark?: boolean;
}

export const CheckboxGroup: <
  T extends string,
  K extends Array<CheckboxGroupOption<T>>,
>(
  props: CheckboxGroupProps<T, K> & {
    /** internal name for ref */
    fieldsetRef?: React.Ref<HTMLFieldSetElement>;
  },
) => React.ReactElement = ({
  value = [],
  onChange,
  title,
  titleDescription,
  fieldsetRef,
  direction = 'row',
  showCheckmark,
  options,
  disabled,
  name,
  ...props
}) => {
  const theme = useTheme();
  return (
    <fieldset
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
      }}
      ref={fieldsetRef}
      {...props}
    >
      <FlexColumnCenterVertical>
        {title && <h3>{title}</h3>}
        {titleDescription && (
          <p style={{ color: theme.colors.textLightOnBg, marginBottom: 0 }}>
            {titleDescription}
          </p>
        )}
      </FlexColumnCenterVertical>

      <div
        style={{
          display: 'flex',
          flexDirection: direction,
          flexWrap: 'wrap',
          gap: '6px',
        }}
      >
        {options.map(({ value: optionValue, label }) => (
          <CheckboxInput
            key={optionValue}
            name={name}
            value={optionValue}
            checked={value?.includes(optionValue) ?? false}
            onChange={(checked: boolean) =>
              checked === true
                ? onChange?.([...value, optionValue])
                : onChange?.(value?.filter((v) => v !== optionValue) || [])
            }
            disabled={disabled}
            showCheckmark={showCheckmark}
          >
            <span>{label}</span>
          </CheckboxInput>
        ))}
      </div>
    </fieldset>
  );
};
